import { useSearchQueryParam } from '@piaas/piaas-ui-toolkit/hooks';
import { useMemo, useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import Auth from '../../api/auth';
import GitlabWikiHelpButton from '../../components/GitlabWikiHelpButton';
import Table from '../../components/Table';
import { roleMessageId } from '../../helpers/lookups';
import useTableApiCall from '../../hooks/useTableApiCall';

import type { TableRowData } from '@/types/tableTypes';
import type { User } from '@/types/user';

const usersGuideSlug = 'user-guide/users';

const searchParamKey = 'governanceUsersSearch';

const Users = () => {
  const intl = useIntl();
  const [rows, setRows] = useState<User[]>([]);
  const navigate = useNavigate();

  const handleRowsUpdate = useCallback(
    (data: unknown[]) => {
      setRows(data as User[]);
    },
    [setRows]
  );

  const [initialSearch] = useSearchQueryParam(searchParamKey);

  const { fetchData, setCurrentPage, itemsPerPage, setItemsPerPage, ...tableData } =
    useTableApiCall(Auth.fetchV2Users, {
      setRows: handleRowsUpdate,
      isPaginated: true,
      searchColumns: ['email', 'roles', 'organization'],
      initialFilter: initialSearch,
      searchParamKey,
      initialItemsPerPage: 5,
    });

  const fields = useMemo(
    () => [
      { title: { id: 'email' }, id: 'email', isAdvancedSearchable: true },
      { title: { id: 'role' }, id: 'roles', isAdvancedSearchable: true },
      {
        title: { id: 'organization' },
        id: 'organization',
        isAdvancedSearchable: true,
      },
    ],
    []
  );

  const transformedRows = useMemo(() => {
    return rows.map(user => ({
      key: user.uuid,
      onRowClick: () => navigate(`/users/${user.uuid}`),
      data: [
        { field: 'email', value: user.email },
        { field: 'roles', value: intl.formatMessage({ id: roleMessageId(user.role) }) },
        { field: 'organization', value: user.organization?.enterpriseName },
      ],
    }));
  }, [navigate, rows, intl]);

  const tableError = tableData.error ? tableData.error.message : null;

  const handlePageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
    },
    [setCurrentPage]
  );

  return (
    <Table
      key={'users-table'}
      header={intl.formatMessage({ id: 'users' })}
      fields={fields}
      rows={transformedRows as TableRowData[]}
      headerActionSlot={{
        content: (
          <GitlabWikiHelpButton
            slug={usersGuideSlug}
            tooltipMessageId='usersGuide'
            tooltipDefaultMessage='Users guide'
            modalTitleMessageId='usersGuide'
            modalTitleDefaultMessage='Users guide'
            buttonAriaLabel={intl.formatMessage({ id: 'usersGuideAriaLabel' })}
            buttonSx={{ mt: 0 }}
          />
        ),
      }}
      {...tableData}
      pageSize={itemsPerPage}
      setItemsPerPage={setItemsPerPage}
      error={tableError}
      setFilter={tableData.setFilter as (filter: unknown) => void}
      onPageChange={handlePageChange}
      advancedSearch={true}
      searchParamKey={searchParamKey}
    />
  );
};

export default Users;
